/* GBI · collection grid (owner K2). Spec: docs/specs/gbi-collection-spec.md §5.6
   <gbi-cgrid>: filters + sort + load more through the Section Rendering API (?section_id=<full id>),
   no full reloads, URL kept in sync, phone filter drawer via GBI.dialog (history-safe).
   Vanilla ES2019, one IIFE, run-once flag, no globals besides it. Needs gbi-core.js (window.GBI). */
(() => {
  'use strict';
  if (window.__gbiCGrid) return;
  window.__gbiCGrid = true;

  const doc = document;
  const DESK = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const G = () => window.GBI || {};
  const track = (name, params) => { const g = G(); if (typeof g.track === 'function') g.track(name, params || {}); };
  const here = () => window.location.pathname + window.location.search;
  // "filter.p.m.custom.build_time" -> "build_time", "filter.v.price.gte" -> "price"
  const shortParam = (name) => String(name || '').replace(/^filter\.(p\.m\.[a-z0-9_]+\.|v\.|p\.)/i, '').replace(/\.(gte|lte)$/, '');

  // Regions replaced wholesale on every response (data-gbi-region="…" inside the section).
  const SWAP_HTML = ['applied', 'prev', 'more'];
  const SWAP_TEXT = ['count', 'drawer-count'];
  const SWAP_ATTR = ['badge', 'badge2', 'aside-clear', 'drawer-clear', 'show'];
  const FORMS = ['groups-aside', 'groups-drawer'];

  class GbiCGrid extends HTMLElement {
    connectedCallback() { this.setup(); }
    disconnectedCallback() { this.teardown(); }

    teardown() {
      if (this._ac) this._ac.abort();
      if (this._req) this._req.abort();
      if (this._io) this._io.disconnect();
      if (this._sentinel && this._sentinel.parentNode) this._sentinel.parentNode.removeChild(this._sentinel);
      clearTimeout(this._priceTimer);
      clearTimeout(this._fvTimer);
      this._ac = this._req = this._io = this._sentinel = null;
    }

    setup() {
      this.teardown();
      const ac = new AbortController();
      this._ac = ac;
      const signal = ac.signal;
      this.sid = this.dataset.sectionId;
      this.base = this.dataset.baseUrl || window.location.pathname;
      this.drawer = this.querySelector('dialog[data-gbi-cfilter]');
      this.cache = new Map();
      this.drawerOpen = false;
      this.pendingUrl = null;
      this.currentUrl = here();

      this.addEventListener('change', (e) => this.onChange(e), { signal });
      this.addEventListener('input', (e) => this.onInput(e), { signal });
      this.addEventListener('click', (e) => this.onClick(e), { signal });
      this.addEventListener('submit', (e) => { e.preventDefault(); this.go(this.urlFrom(e.target)); }, { signal });
      window.addEventListener('popstate', () => this.onPop(), { signal });
      window.addEventListener('pagehide', () => this.savePages(), { signal });
      if (this.drawer) {
        this.drawer.addEventListener('gbi:dialog-close', () => this.onDrawerClose(), { signal });
      }
      const onBp = () => {
        if (DESK.matches && this.drawer && this.drawer.open && G().dialog) G().dialog.close(this.drawer, { reason: 'api' });
        this.initStuck();
      };
      if (DESK.addEventListener) DESK.addEventListener('change', onBp, { signal });

      this.initCollapse(this);
      this.initStuck();
      this.restorePages();
    }

    /* ------------------------------------------------------------ URLs */

    activeForm() {
      if (this.drawerOpen) return this.querySelector('[data-gbi-cfilter-form="drawer"]');
      return this.querySelector('[data-gbi-cfilter-form="aside"]') || this.querySelector('[data-gbi-cfilter-form]');
    }

    // Filters from one form + sort from the toolbar; never page, never section_id, no empty values.
    urlFrom(form) {
      const p = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((v, k) => {
          if (k === 'sort_by' || k === 'page' || v === '') return;
          p.append(k, v);
        });
        // Price From above To: swap them, so "300 – 100" means 100 – 300 instead of "No models match".
        const g = form.querySelector('[data-gbi-price][name$=".gte"]');
        const l = form.querySelector('[data-gbi-price][name$=".lte"]');
        if (g && l && p.has(g.name) && p.has(l.name)) {
          const a = parseFloat(p.get(g.name));
          const b = parseFloat(p.get(l.name));
          if (a > b) { p.set(g.name, String(b)); p.set(l.name, String(a)); }
        }
      }
      const sort = this.querySelector('[data-gbi-sort]');
      if (sort && sort.value && sort.value !== sort.dataset.default) p.set('sort_by', sort.value);
      const qs = p.toString();
      return this.base + (qs ? '?' + qs : '');
    }

    sectionUrl(url) {
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(this.sid);
    }

    /* ------------------------------------------------------------ events */

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-gbi-sort]')) {
        this.syncSortLabel();
        track('sort_use', { value: t.value });
        this.go(this.urlFrom(this.activeForm()), { scroll: true });
        return;
      }
      if (t.matches('[data-gbi-fv]')) {
        this.mirror(t);
        track('filter_use', { type: shortParam(t.name), value: t.value, action: t.checked ? 'add' : 'remove', from: t.form ? t.form.dataset.gbiCfilterForm : '' });
        clearTimeout(this._fvTimer);
        if (this.drawerOpen) {
          // Drawer: quick taps on several values become one request (150 ms). Desktop stays immediate.
          const form = t.form;
          this.setBusy(true);
          this._fvTimer = setTimeout(() => this.go(this.urlFrom(form), { scroll: false }), 150);
        } else {
          this.go(this.urlFrom(t.form), { scroll: true });
        }
      }
    }

    onInput(e) {
      const t = e.target;
      if (!t.matches('[data-gbi-price]')) return;
      clearTimeout(this._priceTimer);
      this._priceTimer = setTimeout(() => {
        track('filter_use', { type: 'price', value: t.name.slice(-3), action: 'set', from: t.form ? t.form.dataset.gbiCfilterForm : '' });
        this.go(this.urlFrom(t.form), { scroll: !this.drawerOpen });
      }, 600);
    }

    onClick(e) {
      const retry = e.target.closest('[data-gbi-retry]');
      if (retry) { e.preventDefault(); if (this._failedUrl) this.go(this._failedUrl, { scroll: false }); return; }
      const open = e.target.closest('[data-gbi-cfilter-open]');
      if (open && this.drawer && G().dialog) {
        e.preventDefault();
        this.drawerOpen = true;
        this.pendingUrl = null;
        G().dialog.open(this.drawer, { opener: open, history: true });
        track('filter_open', { count: Number(this.dataset.count) || 0 });
        return;
      }
      const aside = e.target.closest('[data-gbi-aside-toggle]');
      if (aside) { this.toggleAside(aside); return; }
      const more = e.target.closest('[data-gbi-fmore]');
      if (more) { this.toggleMore(more); return; }
      const a = e.target.closest('a[href]');
      if (!a || !this.contains(a) || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      if (a.matches('[data-gbi-loadmore]')) { e.preventDefault(); this.loadMore(a); return; }
      if (a.matches('[data-gbi-remove]')) {
        e.preventDefault();
        const chips = Array.from(this.querySelectorAll('[data-gbi-remove]'));
        this._focusAfter = { kind: 'chip', index: chips.indexOf(a) };
        track('filter_use', { type: 'chip', value: a.textContent.trim(), action: 'remove', from: 'applied' });
        this.go(a.getAttribute('href'), { scroll: true });
        return;
      }
      if (a.matches('[data-gbi-clear]')) {
        e.preventDefault();
        if (a.getAttribute('aria-disabled') === 'true') return;
        this._focusAfter = { kind: this.drawerOpen ? 'drawer' : 'count' };
        track('filter_clear', { from: this.drawerOpen ? 'drawer' : (a.closest('.gbi-capplied') ? 'applied' : 'aside') });
        this.go(a.getAttribute('href'), { scroll: !this.drawerOpen });
      }
    }

    onPop() {
      if (this.drawerOpen) return;            // the drawer's own history step (GBI.dialog)
      const url = here();
      if (url === this.currentUrl) return;    // another dialog's step (header menu) or a hash change
      this.go(url, { history: false });
    }

    onDrawerClose() {
      this.drawerOpen = false;
      this.showError(false);
      if (this._fvTimer) {
        // A debounced change not sent yet: send it now; go() commits its URL (drawer closed).
        clearTimeout(this._fvTimer);
        this._fvTimer = null;
        this.go(this.urlFrom(this.querySelector('[data-gbi-cfilter-form="drawer"]')), { scroll: true });
        return;
      }
      const url = this.pendingUrl;
      this.pendingUrl = null;
      // A request still running: go() commits its own URL now that the drawer is closed.
      if (this._req) { this._scrollAfter = true; return; }
      // GBI.dialog has already stepped back over its own entry: one new entry for the whole drawer session.
      if (url && url !== here()) {
        try { window.history.pushState({ gbiCGrid: this.sid }, '', url); } catch (e) { /* ignore */ }
        this.scrollToGrid();
      }
    }

    /* ------------------------------------------------------------ fetch + render */

    async go(url, opts) {
      opts = opts || {};
      if (this._req) this._req.abort();
      const ctrl = new AbortController();
      this._req = ctrl;
      this.setBusy(true);
      clearTimeout(this._fvTimer);
      this._fvTimer = null;
      this.showError(false);
      let html = this.cache.get(url);
      if (!html) {
        try {
          let res = await fetch(this.sectionUrl(url), { signal: ctrl.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (res.status === 429) {
            // Rate limited: wait Retry-After (max 3 s, default 1 s) and try once more.
            const ra = parseFloat(res.headers.get('Retry-After'));
            const wait = Math.min(3000, ra > 0 ? ra * 1000 : 1000);
            await new Promise((ok) => setTimeout(ok, wait));
            if (ctrl.signal.aborted) return;
            res = await fetch(this.sectionUrl(url), { signal: ctrl.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          }
          if (!res.ok) throw new Error('status ' + res.status);
          html = await res.text();
        } catch (err) {
          if ((err && err.name === 'AbortError') || ctrl.signal.aborted) return;
          if (this.drawerOpen) {
            // Keep the drawer and the shopper's place: inline message + Try again (spec §5.4.2).
            this._req = null;
            this._failedUrl = url;
            this.setBusy(false);
            this.showError(true);
            return;
          }
          // Desktop / closed drawer: the plain page always works.
          window.location.assign(url);
          return;
        }
        this.cache.set(url, html);
        if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);   // keep the 24 latest
      }
      if (ctrl !== this._req) return;
      this._req = null;
      this.loadedTo = 0;                     // a new result set starts at its first page
      this.render(html);
      this.setBusy(false);
      this.currentUrl = url;
      if (this.drawerOpen) {
        this.pendingUrl = url;               // committed on close (history trap, spec §5.6.4)
      } else if (opts.history !== false && url !== here()) {
        try { window.history.pushState({ gbiCGrid: this.sid }, '', url); } catch (e) { /* ignore */ }
      }
      if (opts.scroll || this._scrollAfter) this.scrollToGrid();
      this._scrollAfter = false;
      this.restoreFocus();
    }

    render(html) {
      const docNew = new DOMParser().parseFromString(html, 'text/html');
      const next = docNew.querySelector('gbi-cgrid');
      if (!next) return;
      this.dataset.count = next.dataset.count || '';
      const pick = (root, name) => root.querySelector('[data-gbi-region="' + name + '"]');

      // grid: <ul> or the empty state, replaced as a whole
      const curGrid = pick(this, 'grid');
      const newGrid = pick(next, 'grid');
      if (curGrid && newGrid) curGrid.replaceWith(doc.importNode(newGrid, true));

      SWAP_HTML.forEach((name) => {
        const c = pick(this, name);
        const n = pick(next, name);
        if (!c || !n) return;
        c.replaceWith(doc.importNode(n, true));
      });
      SWAP_TEXT.forEach((name) => {
        const c = pick(this, name);
        const n = pick(next, name);
        if (c && n && c.textContent !== n.textContent) c.textContent = n.textContent;
      });
      SWAP_ATTR.forEach((name) => {
        const c = pick(this, name);
        const n = pick(next, name);
        if (!c || !n) return;
        ['hidden', 'href', 'disabled', 'aria-disabled', 'tabindex'].forEach((attr) => {
          if (n.hasAttribute(attr)) c.setAttribute(attr, n.getAttribute(attr));
          else c.removeAttribute(attr);
        });
        const cl = c.querySelector('[data-gbi-show-label]');
        const nl = n.querySelector('[data-gbi-show-label]');
        if (cl && nl) cl.textContent = nl.textContent;
        else if (!c.children.length) c.textContent = n.textContent;
      });
      FORMS.forEach((name) => {
        const c = pick(this, name);
        const n = pick(next, name);
        if (c && n) this.morph(c, n);
      });
      // sort: server is the source of truth (e.g. after Back)
      const cs = this.querySelector('[data-gbi-sort]');
      const ns = next.querySelector('[data-gbi-sort]');
      if (cs && ns && cs.value !== ns.value && doc.activeElement !== cs) { cs.value = ns.value; this.syncSortLabel(); }
    }

    // Update the filter form in place: checked, disabled, counts, group badges. The panel keeps its
    // scroll position, open groups and the focused checkbox. Structure changed = replace the groups.
    morph(cur, next) {
      const ci = cur.querySelectorAll('[data-gbi-fv]');
      const ni = next.querySelectorAll('[data-gbi-fv]');
      let same = ci.length === ni.length;
      for (let i = 0; same && i < ci.length; i++) if (ci[i].id !== ni[i].id) same = false;
      if (!same) {
        const open = {};
        cur.querySelectorAll('details[id]').forEach((d) => { open[d.id] = d.open; });
        const focusId = cur.contains(doc.activeElement) ? doc.activeElement.id : '';
        cur.innerHTML = next.innerHTML;
        cur.querySelectorAll('details[id]').forEach((d) => { if (d.id in open) d.open = open[d.id]; });
        this.initCollapse(cur);
        if (focusId) { const f = doc.getElementById(focusId); if (f) f.focus({ preventScroll: true }); }
        return;
      }
      ni.forEach((n, i) => {
        const c = ci[i];
        if (c.checked !== n.checked) c.checked = n.checked;
        c.disabled = n.disabled;
        const cl = cur.querySelector('label[for="' + c.id + '"]');
        const nl = next.querySelector('label[for="' + n.id + '"]');
        if (!cl || !nl) return;
        ['.gbi-cfilter__count', '.gbi-sr'].forEach((sel) => {
          const a = cl.querySelector(sel);
          const b = nl.querySelector(sel);
          if (a && b && a.textContent !== b.textContent) a.textContent = b.textContent;
        });
      });
      cur.querySelectorAll('details[id]').forEach((d) => {
        const nd = next.querySelector('details[id="' + d.id + '"]');
        const a = d.querySelector('[data-gbi-nsel]');
        const b = nd && nd.querySelector('[data-gbi-nsel]');
        if (a && b) { a.textContent = b.textContent; a.hidden = b.hidden; }
      });
      cur.querySelectorAll('[data-gbi-price]').forEach((inp) => {
        const n = next.querySelector('[name="' + inp.name + '"]');
        if (n && doc.activeElement !== inp) inp.value = n.value;
      });
    }

    // Keep the hidden twin form in step at once (desktop ↔ phone share the state).
    mirror(input) {
      const other = this.querySelectorAll('[data-gbi-fv][name="' + CSS.escape(input.name) + '"]');
      other.forEach((o) => { if (o !== input && o.value === input.value) o.checked = input.checked; });
    }

    setBusy(on) {
      this.classList.toggle('is-loading', on);
      const grid = this.querySelector('[data-gbi-region="grid"]');
      if (grid) grid.setAttribute('aria-busy', on ? 'true' : 'false');
      const show = this.querySelector('[data-gbi-region="show"]');
      if (show) show.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    restoreFocus() {
      const f = this._focusAfter;
      this._focusAfter = null;
      if (!f) return;
      let target = null;
      if (f.kind === 'chip') {
        const chips = this.querySelectorAll('[data-gbi-remove]');
        target = chips[Math.min(f.index, chips.length - 1)] || this.querySelector('.gbi-capplied [data-gbi-clear]');
      } else if (f.kind === 'drawer') {
        target = this.drawer && this.drawer.querySelector('.gbi-cfilter__title');
      }
      if (!target) {
        target = this.querySelector('[data-gbi-region="count"]');
        if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
    }

    scrollToGrid() {
      const top = this.getBoundingClientRect().top + window.scrollY;
      // Land the toolbar right under the header (64 phone / 88 desktop): the active-filter row stays visible.
      const offset = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--gbi-sticky-top')) || 0;
      if (window.scrollY > top - offset + 4) {
        window.scrollTo({ top: Math.max(0, top - offset), behavior: REDUCED.matches ? 'auto' : 'smooth' });
      }
    }

    syncSortLabel() {
      const sel = this.querySelector('[data-gbi-sort]');
      const out = this.querySelector('[data-gbi-sort-value]');
      if (sel && out && sel.selectedIndex > -1) out.textContent = sel.options[sel.selectedIndex].text;
      const wrap = sel && sel.closest('.gbi-ctool__sort');
      if (wrap) wrap.classList.toggle('is-set', sel.value !== sel.dataset.default);
    }

    showError(on) {
      const box = this.drawer && this.drawer.querySelector('[data-gbi-cfilter-error]');
      if (!box) return;
      box.hidden = !on;                      // role="alert": announced when shown; focus stays on the value
    }

    /* ------------------------------------------------------------ load more */

    async loadMore(a, opts) {
      opts = opts || {};
      if (a.getAttribute('aria-busy') === 'true') return false;
      a.setAttribute('aria-busy', 'true');
      const url = a.getAttribute('href');
      let html;
      try {
        const res = await fetch(this.sectionUrl(url), { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('status ' + res.status);
        html = await res.text();
      } catch (err) {
        window.location.assign(url);
        return false;
      }
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('gbi-cgrid');
      const list = this.querySelector('ul[data-gbi-region="grid"]');
      const newList = next && next.querySelector('ul[data-gbi-region="grid"]');
      const newMore = next && next.querySelector('[data-gbi-region="more"]');
      if (!list || !newList || !newMore) { window.location.assign(url); return false; }
      const items = Array.from(newList.children).map((li) => doc.importNode(li, true));
      items.forEach((li) => { li.classList.add('is-new'); list.appendChild(li); });
      const more = this.querySelector('[data-gbi-region="more"]');
      const nm = doc.importNode(newMore, true);
      more.replaceWith(nm);                                 // the "previous" region above the grid stays as it is
      const page = Number(nm.dataset.page) || 0;
      this.loadedTo = page;
      track('load_more', { page: page });
      if (!opts.silent && items[0]) {
        const link = items[0].querySelector('a[href]');
        if (link) { try { link.focus({ preventScroll: true }); } catch (e) { link.focus(); } }
      }
      return true;
    }

    // Back from a product page without bfcache: bring back the pages that were loaded (max 4).
    savePages() {
      try {
        const more = this.querySelector('[data-gbi-region="more"]');
        const first = Number(new URLSearchParams(window.location.search).get('page')) || 1;
        const to = this.loadedTo || 0;
        const key = 'gbi-cgrid:' + here();
        if (to > first) sessionStorage.setItem(key, JSON.stringify({ to: to, y: window.scrollY }));
        else sessionStorage.removeItem(key);
        if (!more) sessionStorage.removeItem(key);
      } catch (e) { /* storage blocked */ }
    }

    async restorePages() {
      let saved = null;
      try {
        const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (!nav || nav.type !== 'back_forward') return;
        saved = JSON.parse(sessionStorage.getItem('gbi-cgrid:' + here()) || 'null');
      } catch (e) { return; }
      if (!saved || !saved.to) return;
      for (let guard = 0; guard < 4; guard++) {
        const a = this.querySelector('[data-gbi-loadmore]');
        if (!a || (this.loadedTo || 0) >= saved.to) break;
        const ok = await this.loadMore(a, { silent: true });
        if (!ok) return;
      }
      window.scrollTo(0, saved.y || 0);
    }

    /* ------------------------------------------------------------ small parts */

    toggleAside(btn) {
      const layout = this.querySelector('[data-gbi-layout]');
      if (!layout) return;
      const hidden = layout.classList.toggle('is-aside-hidden');
      btn.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      const lab = btn.querySelector('[data-gbi-aside-label]');
      if (lab) lab.textContent = hidden ? btn.dataset.labelShow : btn.dataset.labelHide;
      track('filter_panel', { state: hidden ? 'hidden' : 'shown' });
    }

    initCollapse(scope) {
      scope.querySelectorAll('[data-gbi-fmore]').forEach((btn) => {
        const list = btn.parentNode.querySelector('.gbi-cfilter__opts');
        if (!list || btn.__gbiInit) return;
        btn.__gbiInit = true;
        list.classList.add('is-collapsed');
        btn.hidden = false;
        btn.setAttribute('aria-expanded', 'false');
      });
    }

    toggleMore(btn) {
      const list = btn.parentNode.querySelector('.gbi-cfilter__opts');
      if (!list) return;
      const collapsed = list.classList.toggle('is-collapsed');
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      btn.textContent = collapsed ? btn.dataset.labelMore : btn.dataset.labelLess;
    }

    // Phones/tablets: .is-stuck while the toolbar sits under the header (paints the band behind the pill).
    initStuck() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      const bar = this.querySelector('[data-gbi-ctool]');
      if (!bar || DESK.matches || !('IntersectionObserver' in window)) { if (bar) bar.classList.remove('is-stuck'); return; }
      if (!this._sentinel) {
        this._sentinel = doc.createElement('div');
        this._sentinel.setAttribute('aria-hidden', 'true');
        // The theme hides div:empty (base.css): force display, or the sentinel reads top 0 = "stuck".
        this._sentinel.style.cssText = 'display:block!important;height:1px;margin-bottom:-1px;pointer-events:none;visibility:hidden;';
        bar.parentNode.insertBefore(this._sentinel, bar);
      }
      const top = parseFloat(getComputedStyle(bar).top) || 0;
      this._io = new IntersectionObserver((entries) => {
        const en = entries[0];
        bar.classList.toggle('is-stuck', !en.isIntersecting && en.boundingClientRect.top < top + 1);
      }, { rootMargin: '-' + Math.round(top + 1) + 'px 0px 0px 0px', threshold: [0, 1] });
      this._io.observe(this._sentinel);
    }
  }

  if (!window.customElements.get('gbi-cgrid')) window.customElements.define('gbi-cgrid', GbiCGrid);
})();
